<script setup>
/**
 * PhotoSnippetBridge — Invisible component that lives in the Snippet module.
 *
 * Listens to Photo-module events and automatically creates/stops/deletes
 * snippets so that every photo capture gets a matching snippet entry.
 *
 * Events consumed (all via EventManager on the current rtcId):
 *   PhotoPanelTakePhotoOn  → start a new snippet (photo capture begins)
 *   PhotoPanelTakePhotoOff → cancel/delete the snippet (photo cancelled/panel closed)
 *   Events.PhotoTaken      → stop the snippet   (photo uploaded)
 *   Events.PhotoDeleted    → delete the snippet  (photo deleted)
 */
import { onMounted, onUnmounted } from "vue";
import { EventManager, Events } from "@events";
import axios from "axios";
import { useRecordingStartTime } from "../composables/useRecordingStartTime";

// Photo module helpers – we import only the event constants, no code changes in Photo module.
import { PhotoPanelEvents } from "../../Photo/helpers/photoPanelActions.js";

const props = defineProps({
  rtcId: {
    type: String,
    required: true,
  },
  sectionType: {
    type: String,
    required: false,
  },
  modelValue: {
    type: Object,
    required: false,
  },
});

const { getElapsedSeconds, setupListener: setupRecordingListener } = useRecordingStartTime(props.rtcId);

// Map: "documentTypeId|documentTypeFaceId" → { snippetId, docTypeName, faceName, stopped }
const pendingSnippets = new Map();

let eventEmitter = null;
const cleanupFns = [];

onMounted(async () => {
  eventEmitter = EventManager.getEventEmitter(props.rtcId);

  // Set up recording start time tracking — await ensures time is captured
  // before we register photo listeners (matches original await behavior)
  const cleanupRecording = await setupRecordingListener();
  cleanupFns.push(cleanupRecording);

  // 1) Photo capture begins → start snippet
  const onPhotoOn = async ({ documentTypeFace }) => {
    const docTypeId = documentTypeFace?.documentType?.id ?? documentTypeFace?.documentTypeId;
    const faceId = documentTypeFace?.id;
    if (!docTypeId || !faceId) return;

    const key = `${docTypeId}|${faceId}`;
    const docTypeName = documentTypeFace?.documentType?.name ?? docTypeId;
    const faceName = documentTypeFace?.name ?? faceId;

    const elapsedSeconds = getElapsedSeconds();

    try {
      const response = await axios.post(`/api/Snippet/StartSnippet/${props.rtcId}`, {
        startSecond: elapsedSeconds,
        description: `${docTypeName} - ${faceName}`,
      });

      if (response.status === 200 && response.data?.success) {
        pendingSnippets.set(key, {
          snippetId: response.data.data?.id,
          docTypeName,
          faceName,
          stopped: false,
        });
      }
    } catch (error) {
      console.error("[PhotoSnippetBridge] Failed to start snippet for photo", error);
    }
  };

  // 2) Photo panel closed / cancelled → delete any pending (unstopped) snippets
  // PhotoPanelTakePhotoOff fires both on cancel AND after successful upload,
  // but by the time it fires on success the snippet is already marked stopped.
  async function cancelPendingSnippets() {
    // Snapshot entries to avoid mutation during async iteration
    const entries = [...pendingSnippets.entries()];
    for (const [key, pending] of entries) {
      if (pending.stopped) continue;
      try {
        await axios.delete(`/api/Snippet/DeleteSnippet/${props.rtcId}/${pending.snippetId}`);
      } catch (error) {
        console.error("[PhotoSnippetBridge] Failed to cancel snippet for photo", error);
      }
      pendingSnippets.delete(key);
    }
  }

  // 3) Photo uploaded → stop snippet
  const onPhotoTaken = async ({ documentTypeId, documentTypeFaceId }) => {
    const key = `${documentTypeId}|${documentTypeFaceId}`;
    const pending = pendingSnippets.get(key);
    if (!pending || pending.stopped) return;

    // Mark stopped BEFORE the async call so cancelPendingSnippets (which may
    // fire during the await via PhotoPanelTakePhotoOff) won't delete it.
    pending.stopped = true;

    const elapsedSeconds = getElapsedSeconds();

    try {
      await axios.post(`/api/Snippet/StopSnippet/${props.rtcId}/${pending.snippetId}`, {
        endSecond: elapsedSeconds,
        description: `${pending.docTypeName} - ${pending.faceName}`,
      });
    } catch (error) {
      console.error("[PhotoSnippetBridge] Failed to stop snippet for photo", error);
      pending.stopped = false; // Allow retry / cleanup on failure
    }
    // Keep the entry so we can delete it later if photo is deleted
  };

  // 4) Photo deleted → delete the related snippet
  const onPhotoDeleted = async ({ documentTypeId, documentTypeFaceId }) => {
    const key = `${documentTypeId}|${documentTypeFaceId}`;
    const pending = pendingSnippets.get(key);
    if (!pending) return;

    try {
      await axios.delete(`/api/Snippet/DeleteSnippet/${props.rtcId}/${pending.snippetId}`);
    } catch (error) {
      console.error("[PhotoSnippetBridge] Failed to delete snippet for photo", error);
    }

    pendingSnippets.delete(key);
  };

  eventEmitter.on(PhotoPanelEvents.PhotoPanelTakePhotoOn, onPhotoOn);
  eventEmitter.on(PhotoPanelEvents.PhotoPanelTakePhotoOff, cancelPendingSnippets);
  eventEmitter.on(Events.PhotoTaken, onPhotoTaken);
  eventEmitter.on(Events.PhotoDeleted, onPhotoDeleted);

  cleanupFns.push(() => {
    eventEmitter.off(PhotoPanelEvents.PhotoPanelTakePhotoOn, onPhotoOn);
    eventEmitter.off(PhotoPanelEvents.PhotoPanelTakePhotoOff, cancelPendingSnippets);
    eventEmitter.off(Events.PhotoTaken, onPhotoTaken);
    eventEmitter.off(Events.PhotoDeleted, onPhotoDeleted);
  });
});

onUnmounted(() => {
  cleanupFns.forEach((fn) => fn());
});
</script>

<template>
  <!-- Invisible bridge component — no UI -->
</template>
